import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import ProfileCompletionModal from './ProfileCompletionModal';
import { 
    Coins, TrendingUp, Calendar, Award, Gift, 
    Users, Share2, CheckCircle, Loader, AlertCircle,
    ArrowUpRight, Sparkles, Clock, Target, Shield,
    BarChart3, PieChart as PieChartIcon, User
} from 'lucide-react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Line, Pie } from 'react-chartjs-2';
import './TokenDashboard.css';
import WalletConnect from './WalletConnect';
import TokenClaim from './TokenClaim';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const TokenDashboard = ({ userToken, userId }) => {
    const navigate = useNavigate();

    const [allocations, setAllocations] = useState([]);
    const [balance, setBalance] = useState({
        active_balance: 0,
        total_allocated: 0,
        frozen_balance: 0,
        verification_locked: false
    });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState('all');
    const [timeRange, setTimeRange] = useState('weekly');
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [profileStatus, setProfileStatus] = useState(null);
    
    // 🆕 NEW: Blockchain allocation states
    const [blockchainAllocation, setBlockchainAllocation] = useState(null);
    const [tgeActivated, setTgeActivated] = useState(false);
    const [walletStatus, setWalletStatus] = useState(null);
    
    // Chart refs
    const earningsChartRef = useRef(null);
    const rewardTypeChartRef = useRef(null);
    useEffect(() => {
        if (userToken && userId) {
            fetchTokenData();
            fetchProfileStatus();
            fetchBlockchainAllocation();
            fetchWalletStatus();
        } else {
            console.warn('⚠️ Missing required data:', { userToken: !!userToken, userId });
            setIsLoading(false);
        }
    }, [userId, filter, userToken]);

    // 🆕 NEW: Fetch wallet status
    const fetchWalletStatus = async () => {
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/blockchain/wallet/status`,
                {
                    headers: {
                        'Authorization': `Bearer ${userToken}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            if (res.ok) {
                const data = await res.json();
                setWalletStatus(data);
            }
        } catch (error) {
            console.error('Error fetching wallet status:', error);
        }
    };

    const fetchProfileStatus = async () => {
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/user/profile/completion-status`,
                {
                    headers: {
                        'Authorization': `Bearer ${userToken}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            
            if (res.ok) {
                const data = await res.json();
                setProfileStatus(data.data);
                
                // Auto-show modal if not completed and not rewarded
                if (!data.data.isCompleted && !data.data.isRewarded) {
                    // Don't auto-show immediately, let user explore first
                    // setShowProfileModal(true);
                }
            }
        } catch (error) {
            console.error('Error fetching profile status:', error);
        }
    };

    const handleProfileComplete = (rewardAmount) => {
        // Close modal
        setShowProfileModal(false);
        
        // Refresh data after profile completion
        fetchTokenData();
        fetchProfileStatus();
        
        // Show success notification (you can replace with a toast library later)
        if (rewardAmount) {
            setTimeout(() => {
                alert(`🎉 Congratulations! You've earned ${rewardAmount} TPHILE tokens!`);
            }, 200);
        }
    };

    // 🆕 NEW: Fetch blockchain allocation from smart contract
    const fetchBlockchainAllocation = async () => {
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/blockchain/wallet/allocation`,
                {
                    headers: {
                        'Authorization': `Bearer ${userToken}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.message || 'Failed to fetch blockchain allocation');
            }

            const data = await res.json();
            console.log('📊 Blockchain Allocation:', data);
            
            setBlockchainAllocation(data.data);
            setTgeActivated(data.data?.allocation?.tge_activated || data.data?.tge_activated || false);

        } catch (error) {
            console.error('❌ Error fetching blockchain allocation:', error);
            
            // Set default allocation if API not ready yet
            setBlockchainAllocation({
                allocation: {
                    total_allocated: 0,
                    instant_amount: 0,
                    vested_amount: 0,
                    claimed_instant: false,
                    claimed_vested: 0,
                    tge_activated: false
                }
            });
        }
    };

    

    const fetchTokenData = async () => {
        setIsLoading(true);
        setError('');
        
        // 🔍 Add validation checks
        console.log('🔍 TokenDashboard Debug:', {
            userToken: userToken ? 'Present' : 'Missing',
            userId: userId,
            apiUrl: process.env.REACT_APP_API_URL
        });
    
        if (!userToken) {
            setError('Authentication token is missing');
            setIsLoading(false);
            return;
        }
    
        if (!userId) {
            setError('User ID is missing');
            setIsLoading(false);
            return;
        }
        
        try {
            // Fetch balance
            const balanceUrl = `${process.env.REACT_APP_API_URL}/ftap/balance?userId=${userId}`;
            console.log('📡 Fetching balance from:', balanceUrl);
            
            const balanceRes = await fetch(balanceUrl, {
                headers: {
                    'Authorization': `Bearer ${userToken}`,
                    'Content-Type': 'application/json'
                }
            });
            
            console.log('📊 Balance Response Status:', balanceRes.status);
            
            if (!balanceRes.ok) {
                const errorText = await balanceRes.text();
                console.error('❌ Balance API Error:', errorText);
                throw new Error(`Failed to fetch balance: ${balanceRes.status} - ${errorText}`);
            }
            
            const balanceData = await balanceRes.json();
            console.log('✅ Balance Data:', balanceData);
            console.log('🔍 Balance Structure:', {
                raw: balanceData,
                data: balanceData.data,
                active_balance_direct: balanceData.active_balance,
                active_balance_data: balanceData.data?.active_balance,
                type: typeof balanceData
            });
            
            // Set the balance correctly
            const extractedBalance = balanceData.data || balanceData;
            console.log('🎯 Setting Balance To:', extractedBalance);
            setBalance(extractedBalance);
            
    
            // Fetch allocations
            const allocationsUrl = `${process.env.REACT_APP_API_URL}/ftap/allocations?userId=${userId}${filter !== 'all' ? `&type=${filter}` : ''}`;
            console.log('📡 Fetching allocations from:', allocationsUrl);
            
            const allocationsRes = await fetch(allocationsUrl, {
                headers: {
                    'Authorization': `Bearer ${userToken}`,
                    'Content-Type': 'application/json'
                }
            });
            
            console.log('📊 Allocations Response Status:', allocationsRes.status);
            
            if (!allocationsRes.ok) {
                const errorText = await allocationsRes.text();
                console.error('⚠️ Allocations API Error:', errorText);
                // Don't throw here - just set empty allocations
                setAllocations([]);
            } else {
                const allocationsData = await allocationsRes.json();
                console.log('✅ Allocations Data:', allocationsData);
                setAllocations(allocationsData.data || allocationsData || []);
            }
    
        } catch (err) {
            console.error('❌ Error fetching token data:', err);
            setError(err.message || 'Failed to load token data');
        } finally {
            setIsLoading(false);
        }
    };

    const getAllocationIcon = (type) => {
        switch (type) {
            case 'prediction_reward':
                return <Target size={18} />;
            case 'referral_bonus':
                return <Users size={18} />;
            case 'social_share':
                return <Share2 size={18} />;
            case 'profile_completion':
                return <CheckCircle size={18} />;
            case 'campaign_bonus':
                return <Gift size={18} />;
            default:
                return <Coins size={18} />;
        }
    };

    const getAllocationLabel = (type) => {
        const labels = {
            'prediction_reward': 'Quality Prediction',
            'referral_bonus': 'Referral Bonus',
            'social_share': 'Social Share',
            'profile_completion': 'Profile Completion',
            'campaign_bonus': 'Campaign Bonus'
        };
        return labels[type] || type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    };
    // Process data for earnings over time chart
    const getEarningsChartData = () => {
        if (!allocations.length) return null;

        const now = new Date();
        let groupedData = {};
        let labels = [];
        
        // Group allocations by date based on timeRange
        allocations.forEach(allocation => {
            const date = new Date(allocation.allocated_at || allocation.created_at);
            let key;
            
            switch(timeRange) {
                case 'daily':
                    key = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    break;
                case 'weekly':
                    const weekStart = new Date(date);
                    weekStart.setDate(date.getDate() - date.getDay());
                    key = weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    break;
                case 'monthly':
                    key = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                    break;
                case 'yearly':
                    key = date.getFullYear().toString();
                    break;
                default:
                    key = date.toLocaleDateString();
            }
            
            if (!groupedData[key]) {
                groupedData[key] = 0;
            }
            groupedData[key] += parseFloat(allocation.tokens_allocated || allocation.amount || 0);
        });

        // Sort and prepare data
        const sortedEntries = Object.entries(groupedData).sort((a, b) => 
            new Date(a[0]) - new Date(b[0])
        );
        
        labels = sortedEntries.map(([key]) => key);
        const data = sortedEntries.map(([, value]) => value);

        return {
            labels,
            datasets: [{
                label: 'TPHILE Earned',
                data,
                borderColor: '#d4af37',
                backgroundColor: 'rgba(212, 175, 55, 0.15)',
                fill: true,
                tension: 0.4,
                pointRadius: 4,
                pointHoverRadius: 6,
                pointBackgroundColor: '#d4af37',
                pointBorderColor: '#000',
                pointBorderWidth: 2,
                pointHoverBackgroundColor: '#f4e5a1',
                pointHoverBorderColor: '#d4af37'
            }]
        };
    };

    
    // Process data for reward type pie chart
    const getRewardTypeChartData = () => {
        if (!allocations.length) return null;

        const rewardTypes = {};
        
        allocations.forEach(allocation => {
            const type = allocation.allocation_type;
            const amount = parseFloat(allocation.tokens_allocated || allocation.amount || 0);
            
            if (!rewardTypes[type]) {
                rewardTypes[type] = 0;
            }
            rewardTypes[type] += amount;
        });

        const labels = Object.keys(rewardTypes).map(type => getAllocationLabel(type));
        const data = Object.values(rewardTypes);
        
        // Gold gradient colors for different reward types
        const colors = {
            'prediction_reward': 'rgba(212, 175, 55, 0.9)',
            'referral_bonus': 'rgba(184, 134, 11, 0.9)',
            'social_share': 'rgba(255, 215, 0, 0.9)',
            'profile_completion': 'rgba(238, 203, 104, 0.9)',
            'campaign_bonus': 'rgba(204, 153, 0, 0.9)'
        };

        const backgroundColors = Object.keys(rewardTypes).map(type => 
            colors[type] || 'rgba(169, 169, 169, 0.8)'
        );

        return {
            labels,
            datasets: [{
                data,
                backgroundColor: backgroundColors,
                borderColor: '#000',
                borderWidth: 2,
                hoverOffset: 8,
                hoverBorderWidth: 3,
                hoverBorderColor: '#d4af37'
            }]
        };
    };

    
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: 'top',
                labels: {
                    color: '#f5f5f5',
                    font: { size: 12, weight: '500' },
                    padding: 15,
                    usePointStyle: true,
                    pointStyle: 'circle'
                }
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.95)',
                titleColor: '#d4af37',
                bodyColor: '#f5f5f5',
                borderColor: '#d4af37',
                borderWidth: 2,
                padding: 12,
                displayColors: true,
                callbacks: {
                    label: function(context) {
                        return `${context.parsed.y || context.parsed} TPHILE`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: { 
                    color: '#d4af37',
                    font: { weight: '500' }
                },
                grid: { 
                    color: 'rgba(212, 175, 55, 0.15)',
                    lineWidth: 1
                }
            },
            x: {
                ticks: { 
                    color: '#ccc',
                    font: { weight: '500' }
                },
                grid: { 
                    color: 'rgba(212, 175, 55, 0.1)',
                    lineWidth: 1
                }
            }
        }
    };

    
    const pieChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: 'right',
                labels: {
                    color: '#f5f5f5',
                    font: { size: 12, weight: '500' },
                    padding: 12,
                    usePointStyle: true,
                    pointStyle: 'circle',
                    generateLabels: (chart) => {
                        const data = chart.data;
                        if (data.labels.length && data.datasets.length) {
                            return data.labels.map((label, i) => {
                                const value = data.datasets[0].data[i];
                                const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
                                const percentage = ((value / total) * 100).toFixed(1);
                                return {
                                    text: `${label}: ${percentage}%`,
                                    fillStyle: data.datasets[0].backgroundColor[i],
                                    fontColor: '#f5f5f5',  // ✨ ADDED THIS
                                    strokeStyle: data.datasets[0].backgroundColor[i],  // ✨ ADDED THIS
                                    hidden: false,
                                    index: i
                                };
                            });
                        }
                        return [];
                    }
                }
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.95)',
                titleColor: '#d4af37',
                bodyColor: '#f5f5f5',
                borderColor: '#d4af37',
                borderWidth: 2,
                padding: 12,
                callbacks: {
                    label: function(context) {
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = ((context.parsed / total) * 100).toFixed(1);
                        return `${context.parsed.toLocaleString()} TPHILE (${percentage}%)`;
                    }
                }
            }
        }
    };

    

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
        });
    };

    if (isLoading) {
        return (
            <div className="token-dashboard">
                <div className="loading-state">
                    <Loader className="spinner-icon" size={48} />
                    <p>Loading your TPHILE tokens...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="token-dashboard">
            {/* Background Effects */}
            <div className="token-bg-pattern"></div>
            <div className="token-ambient-glow"></div>

            {/* Profile Completion Modal */}
            {showProfileModal && (
                <ProfileCompletionModal
    userToken={userToken}
    userId={userId}
    onClose={() => setShowProfileModal(false)}
    onComplete={handleProfileComplete}
/>

            )}

{/* Profile Completion Banner */}
{profileStatus && (
    <div className={`profile-completion-banner ${profileStatus?.isRewarded ? 'completed' : ''}`}>
        <div className="banner-content">
            <div className="banner-icon">
                {profileStatus?.isRewarded ? <CheckCircle size={32} /> : <User size={32} />}
            </div>
            <div className="banner-text">
                {profileStatus?.isRewarded ? (
                    <>
                        <h3>✓ Profile Completed!</h3>
                        <p>
                            You've successfully completed your profile and received <strong>4500 TPHILE</strong> tokens. Thank you for sharing your information!
                        </p>
                    </>
                ) : (
                    <>
                        <h3>Complete Your Profile & Earn Rewards!</h3>
                        <p>
                            Share a bit about yourself and unlock up to <strong>4500 TPHILE tokens</strong>.
                            Your information helps us personalize your experience.
                        </p>
                        <div className="banner-progress">
                            <div className="progress-bar-small">
                                <div 
                                    className="progress-fill-small" 
                                    style={{ width: `${profileStatus.profile_completion_percentage || 0}%` }}
                                ></div>
                            </div>
                            <span className="progress-text">
                                {Math.round(profileStatus.profile_completion_percentage || 0)}% Complete
                            </span>
                        </div>
                    </>
                )}
            </div>
            {profileStatus?.isRewarded ? (
                <div className="reward-claimed-badge">
                    <CheckCircle size={20} />
                    <span>Reward Claimed</span>
                </div>
            ) : (
                <button className="banner-cta" onClick={() => navigate('/profile-completion')}>
                    <Award size={18} />
                    Get Started
                </button>
            )}
        </div>
    </div>
)}


            {/* Header */}
            <div className="token-header">
                <div className="token-title-section">
                    <div className="token-icon-wrapper">
                        <Coins size={36} />
                        <Sparkles className="sparkle-accent" size={20} />
                    </div>
                    <div className="token-title-text">
                        <h1>TPHILE Tokens</h1>
                        <p>Your prediction rewards dashboard</p>
                    </div>
                </div>
            </div>

{/* Balance Cards - Hybrid View */}
<div className="balance-grid">
    {/* CARD 1: Available Balance (Backend + Instant Claimable) */}
    <div className="balance-card primary">
        <div className="balance-header">
            <div className="balance-icon">
                <Coins size={28} />
            </div>
            <span className="balance-label">Available Balance</span>
        </div>
        <div className="balance-amount">
            {parseFloat(balance.active_balance || 0).toLocaleString()}
            <span className="token-symbol">TPHILE</span>
        </div>
        <div className="balance-footer">
            <TrendingUp size={14} />
            <span>Backend Rewards</span>
        </div>
        
        {/* Show blockchain instant claim if available */}
        {blockchainAllocation?.allocation?.instant_amount > 0 && !blockchainAllocation?.allocation?.claimed_instant && tgeActivated && (
    <div className="blockchain-badge instant-available">
        <Shield size={12} />
        <span>+{blockchainAllocation.allocation.instant_amount.toLocaleString()} ready on-chain</span>
    </div>
)}
{blockchainAllocation?.allocation?.instant_amount > 0 && !blockchainAllocation?.allocation?.claimed_instant && !tgeActivated && (
    <div className="blockchain-badge instant-locked">
        <Clock size={12} />
        <span>+{blockchainAllocation.allocation.instant_amount.toLocaleString()} at TGE</span>
    </div>
)}

    </div>

    {/* CARD 2: Total Allocated (Backend + Blockchain Combined) */}
    <div className="balance-card total-combined">
        <div className="balance-header">
            <div className="balance-icon">
                <Award size={24} />
            </div>
            <span className="balance-label">Total Allocated</span>
        </div>
        <div className="balance-amount">
        {(
    parseFloat(balance.total_allocated || 0) + 
    parseFloat(blockchainAllocation?.allocation?.total_allocated || 0)
).toLocaleString()}

            <span className="token-symbol">TPHILE</span>
        </div>
        <div className="balance-breakdown">
            <div className="breakdown-row">
                <span className="breakdown-label">Backend:</span>
                <span className="breakdown-value">{parseFloat(balance.total_allocated || 0).toLocaleString()}</span>
            </div>
            <div className="breakdown-row blockchain">
                <span className="breakdown-label">
                    <Shield size={12} />
                    Blockchain:
                </span>
                <span className="breakdown-value">{parseFloat(blockchainAllocation?.allocation?.total_allocated || 0).toLocaleString()}</span>
            </div>
        </div>
    </div>

    {/* CARD 3: Vesting Progress - Clickable */}
    <div 
        className="balance-card vesting clickable-vesting-card"
        onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('🖱️ Vesting card clicked - navigating to /vesting-details');
            try {
                navigate('/vesting-details');
            } catch (err) {
                console.log('⚠️ React Router failed, using fallback');
                window.location.href = '/vesting-details';
            }
        }}
        onKeyPress={(e) => {
            if (e.key === 'Enter') {
                try {
                    navigate('/vesting-details');
                } catch (err) {
                    window.location.href = '/vesting-details';
                }
            }
        }}
        role="button"
        tabIndex={0}
        aria-label="View vesting details"
        style={{ 
            cursor: 'pointer',
            pointerEvents: 'auto',
            position: 'relative',
            zIndex: 100
        }}
    >
        <div className="balance-header">
            <div className="balance-icon">
                <Calendar size={24} />
            </div>
            <span className="balance-label">VESTING PROGRESS</span>
        </div>
        <div className="balance-amount">
            {parseFloat(blockchainAllocation?.allocation?.vested_amount || 0).toLocaleString()}
            <span className="token-symbol">TPHILE</span>
        </div>
        <div className="vesting-progress-mini">
            <div className="vesting-bar">
                <div 
                    className="vesting-bar-fill" 
                    style={{ 
                        width: `${blockchainAllocation?.allocation?.vested_amount > 0 
                            ? ((blockchainAllocation?.allocation?.claimed_vested || 0) / blockchainAllocation.allocation.vested_amount) * 100 
                            : 0}%` 
                    }}
                ></div>
            </div>
            <span className="vesting-percentage">
                {blockchainAllocation?.allocation?.vested_amount > 0 
                    ? Math.round(((blockchainAllocation?.allocation?.claimed_vested || 0) / blockchainAllocation.allocation.vested_amount) * 100)
                    : 0}% unlocked
            </span>
        </div>

        <div className="balance-footer">
            <Clock size={14} />
            <span>6-month vesting schedule</span>
        </div>
        
        {/* Click indicator arrow */}
        <div className="click-indicator">
            <ArrowUpRight size={18} />
        </div>
    </div>
</div>


            {/* 🔗 Wallet Connect Component */}
            <WalletConnect userToken={userToken} userId={userId} />

            {/* 🆕 Token Claim Component */}
            {blockchainAllocation?.allocation && walletStatus?.walletAddress && (
                <TokenClaim 
                    walletAddress={walletStatus.walletAddress}
                    userToken={userToken}
                    userId={userId}
                />
            )}


            {/* Charts Section */}
            <div className="charts-section">
                <div className="chart-container">
                    <div className="chart-header">
                        <div className="chart-title">
                            <BarChart3 size={24} />
                            <h3>Earnings Over Time</h3>
                        </div>
                        <div className="time-range-buttons">
                            <button 
                                className={`time-btn ${timeRange === 'daily' ? 'active' : ''}`}
                                onClick={() => setTimeRange('daily')}
                            >
                                Daily
                            </button>
                            <button 
                                className={`time-btn ${timeRange === 'weekly' ? 'active' : ''}`}
                                onClick={() => setTimeRange('weekly')}
                            >
                                Weekly
                            </button>
                            <button 
                                className={`time-btn ${timeRange === 'monthly' ? 'active' : ''}`}
                                onClick={() => setTimeRange('monthly')}
                            >
                                Monthly
                            </button>
                            <button 
                                className={`time-btn ${timeRange === 'yearly' ? 'active' : ''}`}
                                onClick={() => setTimeRange('yearly')}
                            >
                                Yearly
                            </button>
                        </div>
                    </div>
                    <div className="chart-canvas-wrapper">
                        {getEarningsChartData() ? (
                            <Line 
                                ref={earningsChartRef}
                                data={getEarningsChartData()} 
                                options={chartOptions} 
                            />
                        ) : (
                            <div className="chart-empty">
                                <BarChart3 size={48} />
                                <p>No earnings data yet</p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="chart-container">
                    <div className="chart-header">
                        <div className="chart-title">
                            <PieChartIcon size={24} />
                            <h3>Reward Types</h3>
                        </div>
                    </div>
                    <div className="chart-canvas-wrapper pie-chart">
                        {getRewardTypeChartData() ? (
                            <Pie 
                                ref={rewardTypeChartRef}
                                data={getRewardTypeChartData()} 
                                options={pieChartOptions} 
                            />
                        ) : (
                            <div className="chart-empty">
                                <PieChartIcon size={48} />
                                <p>No reward data yet</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="allocations-section">
                <div className="section-header">
                    <h2>Transaction History</h2>
                    <div className="filter-buttons">
                        <button 
                            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                            onClick={() => setFilter('all')}
                        >
                            All
                        </button>
                        <button 
                            className={`filter-btn ${filter === 'prediction_reward' ? 'active' : ''}`}
                            onClick={() => setFilter('prediction_reward')}
                        >
                            <Target size={14} />
                            Predictions
                        </button>
                        <button 
                            className={`filter-btn ${filter === 'referral_bonus' ? 'active' : ''}`}
                            onClick={() => setFilter('referral_bonus')}
                        >
                            <Users size={14} />
                            Referrals
                        </button>
                        <button 
                            className={`filter-btn ${filter === 'social_share' ? 'active' : ''}`}
                            onClick={() => setFilter('social_share')}
                        >
                            <Share2 size={14} />
                            Shares
                        </button>
                        <button 
    className="filter-btn profile-btn"
    onClick={() => navigate('/profile-completion')}
>
    <User size={14} />
    Profile Completion
</button>

                    </div>
                </div>

                {/* Allocations List */}
                {error && (
                    <div className="error-banner">
                        <AlertCircle size={20} />
                        <span>{error}</span>
                    </div>
                )}

                {allocations.length === 0 ? (
                    <div className="empty-state">
                        <Coins size={64} className="empty-icon" />
                        <h3>No transactions yet</h3>
                        <p>Start making quality predictions to earn TPHILE tokens!</p>
                    </div>
                ) : (
                    <div className="allocations-list">
                        {allocations.map((allocation) => (
                            <div key={allocation.id} className="allocation-card">
                                <div className="allocation-left">
                                    <div className={`allocation-icon ${allocation.allocation_type}`}>
                                        {getAllocationIcon(allocation.allocation_type)}
                                    </div>
                                    <div className="allocation-info">
                                        <h4>{getAllocationLabel(allocation.allocation_type)}</h4>
                                        <div className="allocation-meta">
                                            <Calendar size={12} />
                                            <span>{formatDate(allocation.allocated_at)}</span>
                                            {allocation.reliability_score && (
                                                <>
                                                    <span className="meta-divider">•</span>
                                                    <Shield size={12} />
                                                    <span>Quality: {allocation.reliability_score}/100</span>
                                                </>
                                            )}
                                            {allocation.metadata?.category && (
                                                <>
                                                    <span className="meta-divider">•</span>
                                                    <span>{allocation.metadata.category}</span>
                                                </>
                                            )}
                                        </div>
                                        {allocation.prediction_text && (
                                            <p className="allocation-description">
                                                {allocation.prediction_text.substring(0, 80)}
                                                {allocation.prediction_text.length > 80 && '...'}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="allocation-right">
                                    <div className="allocation-amount">
                                        <ArrowUpRight size={16} className="amount-icon" />
                                        <span className="amount-value">
                                            +{parseFloat(allocation.tokens_allocated).toLocaleString()}
                                        </span>
                                        <span className="amount-symbol">TPHILE</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default TokenDashboard;
